/**
 * Multi-site showroom country helpers.
 */

import {
  SHOWROOM_COUNTRY_LABELS,
  SHOWROOM_COUNTRY_TO_SITE_ID,
  SHOWROOMS_LISTING_PATH,
  getSiteConfig,
} from '@amblique/king-living/app/constants/site-config';

/** SITE_CONFIG uses GB; showroom tabs/CMS use UK. */
export const normalizeShowroomCountryCode = (code) => {
  const upper = String(code || '').trim().toUpperCase();

  return upper === 'GB' ? 'UK' : upper;
};

export const isCmsCountriesPopulated = (countries) => {
  return Array.isArray(countries) && countries.length > 0 && typeof countries[0] === 'string';
};

/** Uppercase CMS codes as-is; empty when CMS has no countries. */
export const normalizeShowroomCountryCodes = (countries) => {
  if (!isCmsCountriesPopulated(countries)) return [];

  return countries
    .map((code) => normalizeShowroomCountryCode(code))
    .filter(Boolean);
};

export const SHOWROOM_COUNTRY_QUERY_PARAM = 'country';

export const resolveShowroomCountryFromSearchParams = (searchParams, allowedCodes) => {
  const raw = searchParams?.get?.(SHOWROOM_COUNTRY_QUERY_PARAM);

  if (!raw) return null;

  const normalized = normalizeShowroomCountryCode(raw);
  const allowed = new Set((allowedCodes || []).map(normalizeShowroomCountryCode));

  return allowed.has(normalized) ? normalized : null;
};

export const buildShowroomsListingHref = (countryCode) => {
  const normalized = normalizeShowroomCountryCode(countryCode);

  if (!normalized) return SHOWROOMS_LISTING_PATH;

  return `${SHOWROOMS_LISTING_PATH}?${SHOWROOM_COUNTRY_QUERY_PARAM}=${encodeURIComponent(normalized)}`;
};

/** replaceState only — avoid React Router remount/refetch on listing. */
export const replaceShowroomCountryInUrl = (countryCode) => {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const next = normalizeShowroomCountryCode(countryCode);
  const prev = normalizeShowroomCountryCode(url.searchParams.get(SHOWROOM_COUNTRY_QUERY_PARAM));

  if (next === prev) return;

  if (next) url.searchParams.set(SHOWROOM_COUNTRY_QUERY_PARAM, next);
  else url.searchParams.delete(SHOWROOM_COUNTRY_QUERY_PARAM);

  const search = url.searchParams.toString();

  window.history.replaceState(
    window.history.state,
    '',
    `${search ? `${url.pathname}?${search}` : url.pathname}${url.hash || ''}`
  );
};

/** Current-tab badge: AU uses Admin AU (excl. CN); else Shopper catalog total. */
export const resolveCurrentSiteStoreTotal = ({
  currentCountryCode,
  shopperTotal,
  adminByCountry = {},
}) => {
  if (
    currentCountryCode === 'AU' &&
    typeof adminByCountry?.AU?.total === 'number'
  ) {
    return adminByCountry.AU.total;
  }

  return typeof shopperTotal === 'number' ? shopperTotal : null;
};

/** MS-006 tabs: current first; totals = currentTotal + Admin buckets. */
export const buildShowroomCountryTabs = ({
  currentSiteId,
  cmsCountryCodes,
  adminByCountry = {},
  currentTotal = null,
  adminHydrated = false,
}) => {
  if (!(cmsCountryCodes || []).length) return [];

  const currentCode = normalizeShowroomCountryCode(
    getSiteConfig(currentSiteId)?.countryCode || 'AU'
  );
  const others = (cmsCountryCodes || []).filter((code) => code !== currentCode);
  let codes = currentCode ? [currentCode, ...others] : [...others];

  if (adminHydrated) {
    codes = codes.filter((code) => code === currentCode || Boolean(adminByCountry?.[code]));
  }

  return codes.map((code) => {
    const isCurrentSite = code === currentCode;
    const total = isCurrentSite ?
      (typeof currentTotal === 'number' ? currentTotal : null) :
      (typeof adminByCountry?.[code]?.total === 'number' ? adminByCountry[code].total : null);

    return {
      code,
      label: SHOWROOM_COUNTRY_LABELS[code] || code,
      total,
    };
  });
};

export const getAdminFetchCountryCodes = (cmsCountryCodes, currentSiteId) => {
  const currentCode = normalizeShowroomCountryCode(
    getSiteConfig(currentSiteId)?.countryCode || ''
  );

  return (cmsCountryCodes || []).filter((code) => code !== currentCode);
};

/** Google `components=country:` value (UK→gb, CN→cn). */
export const toGeocodeCountryComponent = (countryCode) => {
  const code = normalizeShowroomCountryCode(countryCode);

  if (code === 'UK') return 'gb';
  if (code === 'CN') return 'cn';

  return code.toLowerCase();
};

/** CMS country code or kl-* → SFCC siteId; empty → currentSiteId. */
export const resolveStoreSiteId = (cmsStoreSiteId, currentSiteId = '') => {
  const fallback = String(currentSiteId || '').trim().toLowerCase();
  const raw = String(cmsStoreSiteId || '').trim();

  if (!raw) return fallback;

  const lower = raw.toLowerCase();

  if (lower.startsWith('kl-')) return lower;

  return SHOWROOM_COUNTRY_TO_SITE_ID[normalizeShowroomCountryCode(raw)] || fallback;
};
